import codes from '../constants/codes';

type ApplicationUserDetails =
    | {
          languageId?: string;
      }
    | null
    | undefined;

const getNumber = (value: number | string): number | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? null : numberValue;
};

export const isFrenchLocale = (
    applicationUserDetails: ApplicationUserDetails,
): boolean => applicationUserDetails?.languageId === codes.languageIdFrCa;

export const getDecimalSeparator = (
    applicationUserDetails: ApplicationUserDetails,
): string => (isFrenchLocale(applicationUserDetails) ? ',' : '.');

export const validateDecimalInput = (
    value: string,
    applicationUserDetails: ApplicationUserDetails,
): boolean => {
    if (value === '') {
        return true;
    }

    const separator = getDecimalSeparator(applicationUserDetails);
    const escapedSeparator = separator === '.' ? '\\.' : separator;
    return new RegExp(`^-?\\d*${escapedSeparator}?\\d*$`).test(value);
};

export const limitDecimalPlaces = (
    value: string,
    applicationUserDetails: ApplicationUserDetails,
    maximumDecimalPlaces: number = 2,
): string => {
    const separator = getDecimalSeparator(applicationUserDetails);
    const [integerPart, decimalPart] = value.split(separator);

    if (decimalPart === undefined) {
        return value;
    }

    return `${integerPart}${separator}${decimalPart.slice(0, maximumDecimalPlaces)}`;
};

export const normalizeDecimalInput = (
    value: string | number,
    applicationUserDetails: ApplicationUserDetails,
): string =>
    isFrenchLocale(applicationUserDetails)
        ? String(value).replace(',', '.')
        : String(value);

export const parseDecimalValue = (
    value: string | number,
    applicationUserDetails: ApplicationUserDetails,
): number | null =>
    getNumber(normalizeDecimalInput(value, applicationUserDetails));

export const formatAmountForInput = (
    value: number | string,
    applicationUserDetails: ApplicationUserDetails,
): string => {
    const numberValue = parseDecimalValue(value, applicationUserDetails);
    if (numberValue === null) {
        return '';
    }

    return numberValue
        .toFixed(2)
        .replace('.', getDecimalSeparator(applicationUserDetails));
};

export const formatAmountForDisplay = (
    value: number | string,
    applicationUserDetails: ApplicationUserDetails,
): string => {
    const numberValue = parseDecimalValue(value, applicationUserDetails);
    if (numberValue === null) {
        return '';
    }

    return new Intl.NumberFormat(
        isFrenchLocale(applicationUserDetails)
            ? codes.languageFrCaCode
            : applicationUserDetails?.languageId === codes.languageIdEnCa
              ? codes.languageEnCaCode
              : codes.languageEnGbCode,
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
    ).format(numberValue);
};

export const formatAmountWithCurrency = (
    value: number | string,
    symbol: string | undefined,
    applicationUserDetails: ApplicationUserDetails,
): string => {
    const formattedAmount = formatAmountForDisplay(
        value,
        applicationUserDetails,
    );
    if (!symbol || !formattedAmount) {
        return formattedAmount;
    }

    return isFrenchLocale(applicationUserDetails)
        ? `${formattedAmount} ${symbol}`
        : `${symbol} ${formattedAmount}`;
};
